import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { AuthService } from "@/services/authService";
import { currentUser } from "@/redux/reducers/AuthReducer";
import { resolveLandingPath } from "@/routes/landing";

export const LinkedinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.03-1.85-3.03-1.85 0-2.13 1.44-2.13 2.93v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.26 2.37 4.26 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14ZM7.12 20.45H3.56V9h3.56v11.45ZM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.72V1.72C24 .77 23.21 0 22.23 0Z"
    />
  </svg>
);

const LinkedinCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);

    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    const storedState = localStorage.getItem("oauth_state");
    localStorage.removeItem("oauth_state");

    if (error) {
      console.error("LinkedIn OAuth error:", error, errorDescription);
      toast.error(errorDescription || error || "LinkedIn sign in failed.");
      navigate("/login?error=linkedin_oauth_error");
      return;
    }

    if (!code || !state) {
      console.error("LinkedIn callback missing code/state:", window.location.href);
      toast.error("LinkedIn sign in was cancelled or failed.");
      navigate("/login?error=oauth_failed");
      return;
    }

    if (!storedState) {
      toast.error("Session expired. Please try signing in again.");
      navigate("/login?error=oauth_failed");
      return;
    }

    AuthService.handleLinkedinCallback(code, state, storedState)
      .then(async (data) => {
        const result = await dispatch(currentUser());

        if (currentUser.fulfilled.match(result)) {
          toast.success(`Welcome, ${data.user?.name || data.user?.email || "there"}!`);
          // Resolve the destination from the account that just signed in.
          // /admin/dashboard is wrong for a SuperAdmin (RouteGuard bounces them
          // to /super-admin/*, showing the tenant shell on the way) and wrong
          // for anyone whose role lacks dashboard.view — they would land on
          // "Unauthorized" straight after a successful sign-in.
          navigate(resolveLandingPath(result.payload?.user), { replace: true });
        } else {
          toast.error("Failed to load user profile. Please try again.");
          navigate("/login?error=oauth_failed");
        }
      })
      .catch((err) => {
        console.error("LinkedIn callback error:", err?.response?.data);
        const msg = err?.response?.data?.message || "LinkedIn sign in failed. Please try again.";
        toast.error(msg);
        navigate("/login?error=oauth_failed");
      });
  }, [dispatch, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] text-white">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="animate-spin text-indigo-400" size={28} />
        <p className="text-sm text-white/60">Completing LinkedIn sign in...</p>
      </div>
    </div>
  );
};

export default LinkedinCallback;
